import { http } from '@/app/lib/http/client'
import type { DiscountSnapshot } from '@/app/lib/pricing-display'

export interface SaleItem {
  id: number
  productName: string
  quantity: number
  price: number
  discount: number
  subtotal: number
  returned: number
}

export interface Sale {
  id: string | number
  ticket_number?: string
  date: string
  time: string
  customer: string
  customer_data?: any
  seller: string
  deliveryPerson?: string
  branch: string
  items: SaleItem[]
  subtotal: number
  tax: number
  total: number
  paymentMethod: string
  invoiced: boolean
  cfdi_id?: string | null
  status: 'completed' | 'partial_return' | 'total_return' | string
  saleType?: 'recharge' | 'service' | 'common'
  returnReason?: string
  returnDate?: string
  return_summary?: {
    returned_items_count: number
    refunded_amount: string
    latest_return_reason: string
    latest_return_at: string
    latest_return_cfdi_id?: string
  }
}

export interface CreateReturnPayload {
  idempotency_key: string
  reason: string
  items: Array<{
    sale_item_id: number
    quantity: number
    restock_to_inventory: boolean
    reason?: string
  }>
}

export interface ConfirmBankReturnPayload {
  reference: string
}

export interface CancelBankReturnPayload {
  reason: string
}

export interface CancelSalePayload {
  idempotency_key: string
  reason: string
  reference?: string
  /** Si false, no cancela el CFDI al cancelar la venta. Default: true */
  cancel_invoice?: boolean
  /** Motivo de cancelación SAT (01-04). Default: '02' */
  cfdi_motive?: '01' | '02' | '03' | '04'
  /** UUID del CFDI sustituto (solo para motivo 01) */
  cfdi_uuid_replacement?: string
}

export interface SaleDetailItem {
  id: number
  product_id: number
  product_name: string
  quantity: string | number
  returned_quantity: string | number
  unit_price: string | number
  discount_amount: string | number
  savings_amount?: string | number
  regular_total?: string | number
  discount_type?: string | null
  promotion_id?: number | null
  discount_snapshot?: DiscountSnapshot | null
  tax_amount?: string | number
  subtotal: string | number
  total: string | number
}

export interface SaleDetailPrescription {
  id: number
  doctor_id: number | null
  doctor: {
    id: number | null
    full_name: string
    professional_license: string
    address_line1: string | null
    address_line2: string | null
    city: string | null
    state: string | null
    zip: string | null
    country: string | null
  }
  prescription_date?: string | null
  fulfillment_type: 'PARTIAL' | 'COMPLETE'
  status: 'DRAFT' | 'DISPENSED' | 'VOIDED'
  controls: Array<{
    regulatory_class: 'GENERAL' | 'ANTIBIOTIC' | 'CONTROLLED'
    folio?: string | null
    folio_source?: 'AUTOMATIC' | 'MANUAL' | null
  }>
}

export interface HistoricalUserActor {
  id: number | null
  source_user_id: number
  name: string
  last_name: string | null
  full_name?: string
  email?: string | null
  role?: {
    id: number | null
    code: number | null
    name: string | null
  }
  is_deleted: boolean
}

export interface SaleDetail {
  id: number
  ticket_number: string
  status: string
  completed_at: string
  customer: {
    id: number | null
    name: string
    phone: string
    email: string
  }
  seller: HistoricalUserActor | null
  performed_by: HistoricalUserActor | null
  sucursal: {
    name: string
  }
  cash_register: {
    name: string
  }
  payment: {
    method: {
      name: string
    }
  } | null
  is_delivery: boolean
  delivery: {
    id: number | null
    status: string | null
    delivery_user: {
      id: number
      name: string
      last_name?: string | null
      phone_number?: string | null
    } | null
    recipient_name: string | null
    recipient_phone: string | null
    address: string | null
    observations: string | null
    delivery_fee_amount: string | number | null
  } | null
  amounts: {
    subtotal: string | number
    discount_total: string | number
    tax_total: string | number
    delivery_fee: string | number
    total: string | number
    regular_total?: string | number
    savings_total?: string | number
  }
  loyalty?: {
    credit_applied: string | number
    external_amount: string | number
    points_redeemed: number
    credit_generated: string | number
    points_earned: number
    points_balance_after?: number | null
    lifetime_points_after?: number | null
    credit_balance_after?: string | number | null
  }
  items: SaleDetailItem[]
  prescriptions: SaleDetailPrescription[]
  returns: any[]
  actions: {
    can_invoice: boolean
    can_return: boolean
    can_cancel: boolean
  }
  cfdi_cancelled_at?: string | null
}

// objeto de servicio con las peticiones
export const salesService = {
  async getSales(params?: {
    per_page?: number;
    search?: string;
    page?: number;
    sucursal_id?: string | number;
    cash_register_id?: string | number;
    cash_register_session_id?: string | number;
    status?: string;
    seller_id?: string | number;
    delivery_user_id?: string | number;
    customer_id?: string | number;
    payment_method_id?: string | number;
    bank_name?: string;
    payment_method?: string;
    invoice_status?: string;
    sale_type?: 'recharge' | 'service' | 'common' | string;
    date_from?: string;
    date_to?: string;
  }): Promise<{ data: Sale[], current_page: number, last_page: number, total: number, from?: number, to?: number, summary?: any, filter_options?: any }> {
    const response = await http.get('/pos/sales', { params })
    const backendData = response.data?.data || {}
    const paginator = backendData.sales || backendData || {}
    const summary = backendData.summary || {}
    const filter_options = backendData.filter_options || {}

    const items: Sale[] = (paginator.data || []).map((backendSale: any) => {
      let date = ''
      let time = ''
      if (backendSale.completed_at) {
        const dateStr = backendSale.completed_at.endsWith('Z') ? backendSale.completed_at : backendSale.completed_at.replace(' ', 'T') + 'Z'
        const d = new Date(dateStr)
        const year = d.getFullYear()
        const month = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        date = `${year}-${month}-${day}`

        const hours = String(d.getHours()).padStart(2, '0')
        const mins = String(d.getMinutes()).padStart(2, '0')
        time = `${hours}:${mins}`
      }

      return {
        id: backendSale.id,
        ticket_number: backendSale.ticket_number,
        date: date,
        time: time,
        customer: backendSale.customer?.name || 'Cliente',
        customer_data: backendSale.customer,
        seller: backendSale.seller ? `${backendSale.seller.name} ${backendSale.seller.last_name || ''}`.trim() : '',
        deliveryPerson: backendSale.delivery?.delivery_user ? `${backendSale.delivery.delivery_user.name} ${backendSale.delivery.delivery_user.last_name || ''}`.trim() : undefined,
        branch: backendSale.sucursal?.name || '',
        items: backendSale.items || [],
        subtotal: parseFloat(backendSale.amounts?.subtotal || 0),
        tax: parseFloat(backendSale.amounts?.tax_total || 0),
        total: parseFloat(backendSale.amounts?.total || 0),
        paymentMethod: backendSale.payment_method?.name || 'Efectivo',
        invoiced: backendSale.invoice?.invoiced || false,
        cfdi_id: backendSale.invoice?.cfdi_id || null,
        status: backendSale.status || 'completed',
        saleType: backendSale.sale_type || 'common',
        return_summary: backendSale.return_summary
      } as Sale
    })

    return {
      data: items,
      current_page: paginator.current_page || 1,
      last_page: paginator.last_page || 1,
      total: paginator.total || 0,
      from: paginator.from,
      to: paginator.to,
      summary: summary,
      filter_options: filter_options
    }
  },

  // obtener venta
  async getSale(id: string | number): Promise<SaleDetail> {
    const response = await http.get(`/pos/sales/${id}`)
    return response.data?.data
  },



  // crear devolucion
  async createReturn(saleId: string | number, payload: CreateReturnPayload) {
    const response = await http.post(`/pos/sales/${saleId}/returns`, payload)
    return response.data
  },

  // actualizar devolucion (confirmar banco)
  async updateBankReturn(returnId: string | number, payload: ConfirmBankReturnPayload) {
    const response = await http.post(`/pos/returns/${returnId}/confirm`, payload)
    return response.data
  },

  // eliminar devolucion (rechazo del banco)
  async deleteBankReturn(returnId: string | number, payload: CancelBankReturnPayload) {
    const response = await http.post(`/pos/returns/${returnId}/cancel`, payload)
    return response.data
  },

  // cancelar venta
  async cancelSale(saleId: string | number, payload: CancelSalePayload) {
    const response = await http.post(`/pos/sales/${saleId}/cancel`, payload)
    return response.data
  },

  // facturar venta post-venta (desde registro de ventas)
  async invoiceSale(saleId: string | number, invoiceReceiver: {
    rfc: string
    name: string
    cfdi_use: string
    fiscal_regime: string
    zip_code: string
    email?: string
  }) {
    const response = await http.post(`/pos/sales/${saleId}/invoice`, {
      invoice_receiver: invoiceReceiver,
    })
    return response.data
  },
}
